import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MapPin, ArrowRight, Lightbulb, Phone, Mail, DollarSign, TrendingUp } from "lucide-react";
import { Lead } from "@/hooks/useLeads";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { LeadScoreBadge, calculateLeadScore } from "@/components/sales/LeadScoreBadge";

interface LeadCardProps {
  lead: Lead;
  isSelected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onConvert: (id: string) => void;
  onCardClick: (id: string) => void;
  isConverting: boolean;
}

interface LeadScore {
  overall_score: number;
  score_breakdown: {
    property_value: number;
    engagement: number;
    recency: number;
    source_quality: number;
    storm_proximity: number;
  };
  ai_recommendation: string;
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    new: "bg-blue-500",
    contacted: "bg-info",
    qualified: "bg-purple-500",
    proposal_sent: "bg-orange-500",
    negotiating: "bg-yellow-500",
    won: "bg-success",
    lost: "bg-destructive",
  };
  return colors[status] || "bg-muted";
};

export const LeadCard = ({ 
  lead, 
  isSelected, 
  onSelect, 
  onConvert, 
  onCardClick,
  isConverting 
}: LeadCardProps) => {
  const [leadScore, setLeadScore] = useState<LeadScore | null>(null);

  useEffect(() => {
    loadLeadScore();
  }, [lead.id]);

  const loadLeadScore = async () => {
    const { data } = await supabase
      .from("lead_scores")
      .select("*")
      .eq("lead_id", lead.id)
      .maybeSingle();

    if (data) {
      setLeadScore(data as unknown as LeadScore);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "bg-green-500/10 text-green-600 border-green-500/20";
    if (score >= 60) return "bg-yellow-500/10 text-yellow-600 border-yellow-500/20";
    if (score >= 40) return "bg-orange-500/10 text-orange-600 border-orange-500/20";
    return "bg-red-500/10 text-red-600 border-red-500/20";
  };

  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-all hover:border-primary/50"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('leadId', lead.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      <CardHeader className="p-3 md:pb-3 md:p-6">
        <div className="flex items-start gap-2 md:gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => onSelect(lead.id, checked as boolean)}
            onClick={(e) => e.stopPropagation()}
            className="mt-1"
          />
          <div className="flex-1 min-w-0" onClick={() => onCardClick(lead.id)}>
            <div className="flex items-center gap-1.5 md:gap-2 mb-2 flex-wrap">
              <Badge className={cn(getStatusColor(lead.status), "text-xs")} variant="default">
                {lead.status.replace('_', ' ')}
              </Badge>
              {lead.source && (
                <span className="text-[10px] md:text-xs text-muted-foreground">
                  via {lead.source}
                </span>
              )}
              {lead.converted_to_job_id && (
                <Badge variant="outline" className="bg-success/10 text-success border-success text-xs">
                  Converted
                </Badge>
              )}
              <LeadScoreBadge score={leadScore?.overall_score || calculateLeadScore(lead)} size="sm" />
            </div>
            <h3 className="font-semibold text-sm md:text-base mb-1.5 md:mb-2 truncate">{lead.contact_name}</h3>
            <div className="space-y-0.5 md:space-y-1 text-xs md:text-sm text-muted-foreground">
              {lead.property_address && (
                <div className="flex items-start gap-1">
                  <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span className="truncate">
                    {lead.property_address}
                    {lead.property_city && `, ${lead.property_city}`}
                  </span>
                </div>
              )}
              {lead.contact_phone && (
                <div className="flex items-center gap-1">
                  <Phone className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{lead.contact_phone}</span>
                </div>
              )}
              {lead.contact_email && (
                <div className="flex items-center gap-1">
                  <Mail className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{lead.contact_email}</span>
                </div>
              )}
              {lead.estimated_value && (
                <div className="flex items-center gap-1 font-semibold text-foreground">
                  <DollarSign className="w-3 h-3 flex-shrink-0" />
                  {lead.estimated_value.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3 md:p-6 pt-0 space-y-2">
        {leadScore?.ai_recommendation && (
          <div className="flex items-start gap-2 p-2 bg-primary/5 rounded-md border border-primary/10">
            <Lightbulb className="w-3 h-3 md:w-4 md:h-4 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-[10px] md:text-xs text-primary font-medium line-clamp-2">{leadScore.ai_recommendation}</p>
          </div>
        )}
        {!lead.converted_to_job_id && lead.status !== 'lost' && (
          <Button
            size="sm"
            className="w-full bg-action hover:bg-action-hover text-xs md:text-sm h-8 md:h-9"
            onClick={(e) => {
              e.stopPropagation();
              onConvert(lead.id);
            }}
            disabled={isConverting}
          >
            <ArrowRight className="w-3 h-3 md:w-4 md:h-4 mr-2" />
            Convert to Job
          </Button>
        )}
      </CardContent>
    </Card>
  );
};